import React from 'react';
import PropTypes from 'prop-types';
import { pickBy } from 'lodash';
import { BackButton } from 'web-ui-components/lib/molecules/navigation';
import ViewChildPaymentHistory from './ViewChildPaymentHistory';
import { renderAccountDetails } from '../ViewPaymentHistory';
import PrintHelper from 'print-helper-lib';
import StatusBadgeSME from './StatusBadgeSME';
import {
  SectionHeading,
  SubHeading,
} from 'web-ui-components/lib/typography/headings';
import { StandardBody } from 'web-ui-components/lib/typography/body';
import { VerticalReviewTable } from 'web-ui-components/lib/layout/tables';
import { NavigationButton } from 'web-ui-components/lib/navigation/buttons';

const brandPathMapping = {
  b: 'DYB',
  cb: 'CB',
  yb: 'YB',
  vm: 'VM',
};

const getBrandId = () => {
  const path = window.location.pathname.slice(1);
  const slashPosition = path.indexOf('/');
  const brand = slashPosition === -1 ? path : path.substring(0, slashPosition);
  return brandPathMapping[`${brand}`.toLowerCase()];
};

export const getChapsHistoryDetails = (historyDetails, content) => {
  const {
    paymentType,
    paymentFees,
    submissionDate,
    amount,
    fromAccountAccountNumber,
    fromAccountSortCode,
    fromAccountName,
    toAccountAccountNumber,
    toAccountName,
    toAccountSortCode,
    reference,
    createdBy,
    createdDate,
    authorisedBy,
  } = historyDetails;
  const tableRowMapping = pickBy({
    [content.typeLabel]: paymentType,
    [content.fromLabel]: renderAccountDetails(
      fromAccountAccountNumber,
      fromAccountSortCode,
      fromAccountName
    ),
    [content.toLabel]: renderAccountDetails(
      toAccountAccountNumber,
      toAccountSortCode,
      toAccountName
    ),
    [content.amount]: amount,
    ...(reference && { [content.reference]: reference }),
    [content.feesLabel]: paymentFees,
    [content.paymentDateLabel]: submissionDate,
    ...(createdBy && { [content.createdByLabel]: createdBy }),
    ...(createdDate && { [content.createdOnLabel]: createdDate }),
    ...(authorisedBy && { [content.authorisedByLabel]: authorisedBy }),
  });
  return tableRowMapping;
};

const ViewChapsHistory = ({
  historyDetails,
  statusMsg,
  goBack,
  content,
  parentPayment = false,
  brandId = getBrandId(),
}) => {
  const tableRowMapping = getChapsHistoryDetails(historyDetails, content);

  const statusData = [
    {
      headerText: 'Status',
      cellText: (
        <div>
          <StatusBadgeSME status={historyDetails.status} />
          <StandardBody>
            <p>{statusMsg}</p>
          </StandardBody>
        </div>
      ),
    },
  ];

  const paymentData = () =>
    Object.keys(tableRowMapping).map((tableRowKey) => ({
      headerText: tableRowKey,
      cellText: tableRowMapping[tableRowKey],
    }));

  return (
    <div className="web-ui-components">
      <BackButton onClick={goBack} />
      <SubHeading>Payment status</SubHeading>
      <div className="u-component-spacing">
        <div className="u-margin-bottom-4">
          <SectionHeading>Bank acknowledgement details</SectionHeading>
          <PrintHelper
            className="web-ui-components"
            brandId={brandId}
            showPrintTopIcon
            showPrintBottomIcon={false}
          />

          <VerticalReviewTable data={statusData} />
        </div>
        <div className="u-margin-bottom-2">
          <SectionHeading>Payment details</SectionHeading>
          <VerticalReviewTable data={paymentData()} />
        </div>

        {parentPayment && historyDetails.children && (
          <ViewChildPaymentHistory historyDetails={historyDetails} />
        )}

        <NavigationButton.Secondary labelText="Back" onClick={goBack} />
      </div>
    </div>
  );
};

ViewChapsHistory.propTypes = {
  statusMsg: PropTypes.string.isRequired,
  historyDetails: PropTypes.shape({
    paymentType: PropTypes.string.isRequired,
    paymentFees: PropTypes.string.isRequired,
    submissionDate: PropTypes.string.isRequired,
    authorisedBy: PropTypes.string.isRequired,
    createdBy: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    amount: PropTypes.string.isRequired,
    fromAccountAccountNumber: PropTypes.string.isRequired,
    fromAccountSortCode: PropTypes.string.isRequired,
    fromAccountName: PropTypes.string.isRequired,
    toAccountAccountNumber: PropTypes.string.isRequired,
    toAccountSortCode: PropTypes.string.isRequired,
    toAccountName: PropTypes.string.isRequired,
    reference: PropTypes.string.isRequired,
  }).isRequired,
  goBack: PropTypes.func.isRequired,
  content: PropTypes.shape({}),
};

export default ViewChapsHistory;
